import React from 'react';
import { useIntl } from 'react-intl';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

export interface ArchiveTarget {
  type: 'template' | 'group' | 'item';
  id: number;
  name: string;
  childCount: number;
}

interface ArchiveConfirmModalProps {
  target: ArchiveTarget | null;
  isSubmitting?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const TYPE_LABEL_ID: Record<ArchiveTarget['type'], string> = {
  template: 'assets.archive.typeLabel.template',
  group: 'assets.archive.typeLabel.group',
  item: 'assets.archive.typeLabel.item',
};

const ArchiveConfirmModal: React.FC<ArchiveConfirmModalProps> = ({
  target,
  isSubmitting = false,
  error = null,
  onConfirm,
  onCancel,
}) => {
  const intl = useIntl();
  if (!target) return null;

  const typeLabel = intl.formatMessage({ id: TYPE_LABEL_ID[target.type] });

  return (
    <Modal isOpen toggle={onCancel} fade={false} centered>
      <ModalHeader toggle={onCancel}>
        {intl.formatMessage({ id: 'assets.archive.confirmTitle' }, { typeLabel })}
      </ModalHeader>
      <ModalBody>
        <p>{intl.formatMessage({ id: 'assets.archive.confirmText' }, { name: target.name })}</p>
        {target.childCount > 0 && (
          <p className="text-muted mb-0">
            {intl.formatMessage(
              { id: 'assets.archive.childWarning' },
              { count: target.childCount, typeLabel }
            )}
          </p>
        )}
        {error && (
          <p className="text-danger mt-3 mb-0">
            {intl.formatMessage({ id: 'assets.archive.failed' })}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onCancel} disabled={isSubmitting}>
          {intl.formatMessage({ id: 'assets.archive.cancel' })}
        </Button>
        <Button color="danger" onClick={onConfirm} disabled={isSubmitting}>
          {intl.formatMessage({ id: 'assets.archive.confirm' })}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ArchiveConfirmModal;
